/**
 * Many trees (BUILDLIST E5.3; lane F): the person-scale biome trees — and the rocks of their stands, fields and
 * mountains — drawn as instances, in chunks, at three tiers of detail, built a few at a time as the camera moves.
 * A rock goes through all of what follows as a tree does, minus the sway and the tufts: the kit's rock kinds in
 * the planet's rock colour, three variants each, seated on the ground's frame as the old rock meshes were.
 *
 * Before: every normal tree was its own model (its own lumpy geometry, a few draws, its ground tufts a
 * few more), swayed on the CPU each frame and cut only beyond 800 people — ~5 draws a tree, a thousand
 * for a battle's stands, and a scene graph that grew by eight objects a tree.
 *
 * Now:
 * - **Prototypes.** Each (tree type × biome) is built in a few seeded variants by the design lane's own
 *   builders (biomeKit `buildTree`, floraModels `buildFloraModel`), merged into one geometry whose colours
 *   are vertex colours and whose glows (the lantern fir's beads) a `glow` attribute — one draw per variant
 *   for every tree that uses it. A tree takes a variant, a yaw and its scale; the look of a stand is the
 *   look it had, give or take which lumps a crown has.
 * - **Tiers.** Within `TREE_LOD.full` people of the camera the full model and its ground tufts; out to
 *   `TREE_LOD.impostor` people a cheap impostor — a five-band lathe fitted to the model's own profile and
 *   colours (a column of bark under a cone or a blob, leaning where the crown leans), ~55 triangles;
 *   beyond, none.
 *   Seen at the full tier's edge a tree is ~30 px tall; at the impostor's edge ~7, where the old drawing
 *   stopped (`NORMAL_PROP_DRAW_PERSONS`).
 * - **Chunks.** Trees are filed by cube-sphere cell (core/cells.ts, ARCHITECTURE §5.3) at a cell about
 *   `TREE_LOD.chunkPersons` people across: the chunk is the unit of culling (frustum and distance, with a
 *   little hysteresis so the tier doesn't flicker at its edge) and of building. Its id is a cell id, the
 *   key E5.4's persistence will use.
 * - **Built within a budget.** A chunk's matrices (each tree seated on the ground as the old meshes were,
 *   groundSeat.ts, and its tufts laid on the ground's plane) are computed the first time it comes in range,
 *   nearest first, `TREE_LOD.budgetMs` a frame — as the fine-terrain patch is built. A crater re-seats the
 *   chunks it touches at once; anything else that moves the ground re-seats its planet's chunks lazily.
 * - **Sway** is in the vertex shader (the old per-tree rotation, per-instance phase from a hash of where
 *   the tree stands), so a frame costs nothing per tree; the instance buffers are repacked only when a
 *   chunk changes tier, comes into view, is built, or a tree is hidden.
 *
 * Draws are per prototype, not per tree: a battle's three biomes come to ~20–40 draws however many trees
 * stand in them. Memory: one 64-byte matrix per tree and per tuft, the prototypes once.
 *
 * Presentation only: reads the state, writes none. Giant (classic) trees keep their own path in renderer.ts,
 * and `?trees=each` (the Lab's **Tree drawing** row) draws normal trees the old way, for an A/B.
 */
import { rockSeatHalf, seatGround } from '../core/propSeat';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { FloraState, GameState, PlanetState, RockState, Vec3 } from '../types';
import { SeededRandom } from '../core/random';
import { PERSON_BODY_HEIGHT, personSetOf } from '../core/scaleProfile';
import { fineGroundLift } from './groundLift';
import { getSurfaceRadius } from '../core/surface';
import { CRATERLET_REACH } from '../core/craterlets';
import { terraceReach } from '../core/terraceGround';
import { fineLiftMaterial } from './groundScatter';
import { basisPlanet, forestCell, forestCellsPerEdge, forestKeepOutAt, planetForestSeed, FOREST, type ForestKeepOut } from '../core/forest';
import { NearFieldStreamer, NEAR_FIELD, type NearFieldClient } from './nearField';
import { JungleField } from './jungleField';
import { BIOMES, biomesFor } from '../core/biomes';
import { cellOf } from '../core/cells';
import { normalize as normalizeVec } from '../math';
import { getFloraWorldPosition, getRockWorldPosition } from '../core/gameState';
import { groundFrame } from '../core/groundFrame';
import { NORMAL_TREE_PERSONS, NORMAL_ROCK_PERSONS, TREE_TRUNK_SIZE } from '../constants';
import { buildTree, buildRock, BIOME_COLORS, BIOME_GROUND, GROUND_TUFT_SCALE, TREE_SPECIES, type TreeSpecies, type RockKind } from './biomeKit';
import { buildFloraModel } from './floraModels';
import { seatGroup, propSeatingOn, type FineGround } from './groundSeat';
import { propHash, rockVariant, rockYaw, treeVariant, treeYaw, TREE_VARIANTS } from '../core/treeDraws';
import { treeFormModel, TREE_FORM, type TreeForm } from './treeForms';
import { PROPS } from '../constants/damage';
import { propForm } from '../core/debris';
import { DebrisView } from './debrisView';
import type { ForestDensityMode } from '../core/forestDensity';
import { FarCanopy, farWoodsDrawn } from './farCanopy';
import { WIND_SWAY, WIND_SWAY_HEAD, WIND_FRONT_GLSL, type WindSwayUniforms } from './windSway';

/** A tree's drawn forms in the order they come (partial destruction step 3 adds the crown lost). */
const FORM_ORDER = ['whole', 'damaged', 'crownLost', 'felled'] as const;
export { treeDrawMode, setTreeDrawMode, TREE_DRAW_MODES, type TreeDrawMode } from './treeDrawMode';

// ---------------------------------------------------------------------------
// The numbers
// ---------------------------------------------------------------------------

/**
 * Distances in person-heights from the camera to a chunk's nearest edge: the full model inside `full`, the
 * impostor out to `impostor`, nothing beyond. A tier holds `hysteresis` (a share of the distance) past its
 * edge before it changes, so a camera resting near an edge doesn't flicker. Chunks are about `chunkPersons`
 * people across; `budgetMs` of building a frame; `variants` seeded variants of each prototype.
 */
/** 62.61: the stands' fades — a streamed chunk grows in over growS seconds; an instance shrinks from cutFrom × the draw's reach to nothing at it. */
export const STANDS_FADE = { growS: 0.5, cutFrom: 0.9 };

/** 62.61: an instanced mesh's instance `i` born at `born` (the stands' clock; never: long ago — drawn whole at once). */
function bornAt(mesh: THREE.InstancedMesh, i: number, born: number | undefined): void {
  const a = mesh.geometry.getAttribute('aBorn') as THREE.InstancedBufferAttribute | undefined;
  if (!a) return;
  (a.array as Float32Array)[i] = born ?? -1e9;
  a.needsUpdate = true;
}
export const TREE_LOD = { full: 200, impostor: 800, hysteresis: 0.06, chunkPersons: 24, budgetMs: 2, variants: TREE_VARIANTS, tuftVariants: 2, frustumMargin: 2, nearMargin: 50 };
/** The full model's sway (radians at the base, as the old per-tree sway); tufts and impostors don't sway. */
export const TREE_SWAY = 0.012;
/** 62.61's follow-up: the forest's cells held past the view's farthest ground by this (person heights): a cell (40) and a stand's reach to spare. */
export const VIEW_REACH_MARGIN_PEOPLE = 60;

export const TIER_FULL = 0, TIER_IMPOSTOR = 1, TIER_NONE = 2;
export type Tier = 0 | 1 | 2;

/** The tier for a chunk `gapPersons` people from the camera, given the tier it had (the hysteresis). Pure. */
export function tierFor(gapPersons: number, prev: Tier): Tier {
  const h = TREE_LOD.hysteresis;
  const fullEdge = TREE_LOD.full * (prev === TIER_FULL ? 1 + h : 1 - h);
  if (gapPersons < fullEdge) return TIER_FULL;
  const impEdge = TREE_LOD.impostor * (prev === TIER_NONE ? 1 - h : 1 + h);
  return gapPersons < impEdge ? TIER_IMPOSTOR : TIER_NONE;
}

/** Cells per cube-face edge for chunks about `persons` people across on a planet of `radius` (a power of two, as core/cells.ts's). */
export function chunkCellsPerEdge(radius: number, person: number, persons = TREE_LOD.chunkPersons): number {
  const want = (2 * radius) / Math.max(1e-9, persons * person);
  let n = 8;
  while (n < 4096 && n * 1.5 < want) n *= 2;
  return n;
}

// ---------------------------------------------------------------------------
// Prototypes: one merged geometry per (type × biome × variant), plus its impostor
// ---------------------------------------------------------------------------

/**
 * Merge a model's opaque meshes into one geometry in the root's frame (the root's own transform included):
 * position and normal, the material's colour as a vertex colour (times any vertex colours it had), and its
 * emissive (× intensity) as `glow`. Transparent parts and points are left out (a spore cloud — specks at this size),
 * except a part that glows (67.31: a lantern bracket's underside goes in, opaque, as its glow). Null when nothing is left.
 */
export function mergeModel(root: THREE.Object3D): THREE.BufferGeometry | null {
  root.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || (mesh as THREE.InstancedMesh).isInstancedMesh || !mesh.visible) return;
    let hidden = false;
    for (let p: THREE.Object3D | null = mesh; p; p = p.parent) if (!p.visible) hidden = true;
    if (hidden) return;
    const mat = mesh.material as THREE.MeshLambertMaterial;
    if (Array.isArray(mat) || !mat) return;
    if (mat.transparent && !(mat.emissive && mat.emissive.getHex() !== 0)) return;   // 67.31: a glowing part stays (its light is the point)
    let g = mesh.geometry.clone();
    if (g.index) g = g.toNonIndexed();
    g.applyMatrix4(mesh.matrixWorld);
    if (!g.attributes.normal) g.computeVertexNormals();
    const n = g.attributes.position.count;
    const col = new Float32Array(n * 3), glow = new Float32Array(n * 3);
    const base = mat.color ?? new THREE.Color(1, 1, 1);
    const own = mat.vertexColors && g.attributes.color ? g.attributes.color as THREE.BufferAttribute : null;
    const e = mat.emissive && mat.emissive.getHex() !== 0 ? mat.emissive.clone().multiplyScalar(mat.emissiveIntensity ?? 1) : null;
    for (let i = 0; i < n; i++) {
      const k = own ? [own.getX(i), own.getY(i), own.getZ(i)] : [1, 1, 1];
      col[i * 3] = base.r * k[0]; col[i * 3 + 1] = base.g * k[1]; col[i * 3 + 2] = base.b * k[2];
      if (e) { glow[i * 3] = e.r; glow[i * 3 + 1] = e.g; glow[i * 3 + 2] = e.b; }
    }
    const out = new THREE.BufferGeometry();
    out.setAttribute('position', g.attributes.position);
    out.setAttribute('normal', g.attributes.normal);
    out.setAttribute('color', new THREE.BufferAttribute(col, 3));
    out.setAttribute('glow', new THREE.BufferAttribute(glow, 3));
    parts.push(out);
    g.dispose();
  });
  if (parts.length === 0) return null;
  const merged = parts.length === 1 ? parts[0] : mergeGeometries(parts, false);
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(), _ab = new THREE.Vector3(), _ac = new THREE.Vector3();

/**
 * The impostor: a lathe fitted to a full tree geometry's own profile (base at y = 0). The tree's height is cut
 * into `bands`; each band gives a ring — at the centre of the band's vertices (so a wind pine's crown leans off
 * its root), as wide as three quarters of them reach, in the band's area-weighted colour — and the rings are
 * joined into a closed hull `sides` round, with an apex at the top. A trunk comes out as a thin column in the
 * bark's colour, a fir as a cone, a broadleaf as a blob on a stalk, whatever the model is; no list of which
 * parts are bark. ~55 triangles.
 */
export function impostorGeometry(full: THREE.BufferGeometry, bands = 5, sides = 5): THREE.BufferGeometry {
  const pos = full.attributes.position as THREE.BufferAttribute, col = full.attributes.color as THREE.BufferAttribute;
  full.computeBoundingBox();
  const y0 = Math.max(0, full.boundingBox!.min.y), top = full.boundingBox!.max.y, dy = Math.max(1e-6, (top - y0) / bands);
  const bandOf = (y: number) => Math.max(0, Math.min(bands - 1, Math.floor((y - y0) / dy)));
  const cx = new Float64Array(bands), cz = new Float64Array(bands), cn = new Float64Array(bands);
  for (let i = 0; i < pos.count; i++) { const b = bandOf(pos.getY(i)); cx[b] += pos.getX(i); cz[b] += pos.getZ(i); cn[b]++; }
  for (let b = 0; b < bands; b++) {
    if (cn[b] > 0) { cx[b] /= cn[b]; cz[b] /= cn[b]; }
    else if (b > 0) { cx[b] = cx[b - 1]; cz[b] = cz[b - 1]; }
  }
  const reach: number[][] = Array.from({ length: bands }, () => []);
  for (let i = 0; i < pos.count; i++) { const b = bandOf(pos.getY(i)); reach[b].push(Math.hypot(pos.getX(i) - cx[b], pos.getZ(i) - cz[b])); }
  const radius = reach.map((r, b) => {
    if (!r.length) return b > 0 ? NaN : 0.002;
    r.sort((p, q) => p - q);
    return Math.max(0.002, r[Math.floor(r.length * 0.75)]);
  });
  for (let b = 1; b < bands; b++) if (Number.isNaN(radius[b])) radius[b] = radius[b - 1];
  // each band's colour, weighted by the area of the triangles centred in it
  const colors = Array.from({ length: bands }, () => new THREE.Color(0, 0, 0)), area = new Float64Array(bands);
  for (let t = 0; t < pos.count / 3; t++) {
    _a.fromBufferAttribute(pos, t * 3); _b.fromBufferAttribute(pos, t * 3 + 1); _c.fromBufferAttribute(pos, t * 3 + 2);
    const w = _ab.subVectors(_b, _a).cross(_ac.subVectors(_c, _a)).length() / 2;
    const b = bandOf((_a.y + _b.y + _c.y) / 3);
    for (let k = 0; k < 3; k++) { colors[b].r += col.getX(t * 3 + k) * w / 3; colors[b].g += col.getY(t * 3 + k) * w / 3; colors[b].b += col.getZ(t * 3 + k) * w / 3; }
    area[b] += w;
  }
  for (let b = 0; b < bands; b++) {
    if (area[b] > 0) colors[b].multiplyScalar(1 / area[b]);
    else colors[b].copy(b > 0 ? colors[b - 1] : new THREE.Color(0.3, 0.25, 0.2));
  }
  // rings: the foot (band 0's spread at the ground), each band's middle, then the apex
  const rings: Array<{ x: number; y: number; z: number; r: number; c: THREE.Color }> = [{ x: cx[0], y: y0, z: cz[0], r: radius[0], c: colors[0] }];
  for (let b = 0; b < bands; b++) rings.push({ x: cx[b], y: y0 + (b + 0.5) * dy, z: cz[b], r: radius[b], c: colors[b] });
  rings.push({ x: cx[bands - 1], y: top, z: cz[bands - 1], r: 0, c: colors[bands - 1] });
  const P: number[] = [], C: number[] = [];
  const at = (ring: typeof rings[number], k: number): [number, number, number] => {
    const a = (k / sides) * Math.PI * 2;
    return [ring.x + Math.cos(a) * ring.r, ring.y, ring.z + Math.sin(a) * ring.r];
  };
  const shade = (ring: typeof rings[number]) => 0.85 + 0.2 * ((ring.y - y0) / Math.max(1e-6, top - y0));
  const push = (v: [number, number, number], ring: typeof rings[number]) => { P.push(...v); const s = shade(ring); C.push(ring.c.r * s, ring.c.g * s, ring.c.b * s); };
  for (let i = 0; i + 1 < rings.length; i++) {
    const lo = rings[i], hi = rings[i + 1];
    for (let k = 0; k < sides; k++) {
      const a = at(lo, k), b = at(lo, k + 1), c = at(hi, k), d = at(hi, k + 1);
      // counter-clockwise seen from outside
      push(a, lo); push(c, hi); push(b, lo);
      if (hi.r > 0) { push(b, lo); push(c, hi); push(d, hi); }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(C, 3));
  g.setAttribute('glow', new THREE.BufferAttribute(new Float32Array(P.length), 3));
  g.computeVertexNormals();
  g.computeBoundingBox();
  g.computeBoundingSphere();
  return g;
}

/** A normal tree's model at scale 1 with no yaw, base at y = 0: the kit's species in the biome's colours, else the flora builder. */
export function treeModel(floraType: string, biome: FloraState['biome'], seed: number): THREE.Group {
  const kit = (TREE_SPECIES as readonly string[]).includes(floraType);
  const g = kit ? buildTree(floraType as TreeSpecies, BIOME_COLORS[biome ?? 'broadleaf'], 1, seed).group : buildFloraModel(floraType as FloraState['floraType'], 1, seed).group;
  g.rotation.set(0, 0, 0);
  g.scale.setScalar(1);
  return g;
}

/** A normal rock's model at scale 1 with no yaw, base at y = 0: the kit's kind in the planet's rock colour. */
export function rockModel(kind: RockKind, color: string, seed: number): THREE.Group {
  const g = buildRock(kind, color, 1, seed);
  g.rotation.set(0, 0, 0);
  g.scale.setScalar(1);
  return g;
}

// ---------------------------------------------------------------------------
// Placement: where a tree and its tufts stand (the old meshes' seats)
// ---------------------------------------------------------------------------

/** Deterministic per-tree draws for the drawing (variant, yaw, fall): a hash of the id, never the sim's streams — core's (E5.2: the sim's tree pieces read the same draws). */
const hashUnit = propHash;

/** The tufts at a tree's foot, as the old meshes laid them (same seeded stream: `flora-ground-<id>`): kind, the model's seed, angle and distance in model units. */
export function treeTufts(flora: Pick<FloraState, 'id' | 'biome'>): Array<{ kind: string; seed: number; angle: number; dist: number; scale: number }> {
  const kinds = BIOME_GROUND[flora.biome ?? 'broadleaf'];
  const rng = new SeededRandom(`flora-ground-${flora.id}`);
  const out = [];
  for (let i = 0, n = rng.int(1, 2); i < n; i++) {
    const kind = kinds[i % kinds.length];
    const angle = rng.range(0, Math.PI * 2), dist = rng.range(0.05, 0.14);
    out.push({ kind, seed: flora.id * 7 + i + 1, angle, dist, scale: GROUND_TUFT_SCALE[kind] ?? 0.2 });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The stands
// ---------------------------------------------------------------------------

interface Proto {
  key: string;
  /** a tree (its flora type, coloured by its biome) or a rock (its kit kind, coloured by the planet's rock) */
  kind: 'tree' | 'rock';
  type: string;
  biome: FloraState['biome'];
  color: string;
  variant: number;
  /** 63.4 step 8: a tree's damaged or destroyed form (lane E's treeFormModel); absent = the whole tree */
  form?: TreeForm;
  /** partial destruction step 3: the damaged form's level (absent = treeFormModel's default) */
  level?: number;
  /** props using it (the instance capacity of each tier) */
  users: number;
  geo: { full: THREE.BufferGeometry; impostor: THREE.BufferGeometry } | null;
  full: THREE.InstancedMesh | null;
  impostor: THREE.InstancedMesh | null;
  /** model-unit extents of the full geometry (the seat needs its height) */
  height: number;
  half: number;
}
interface TuftProto { key: string; kind: string; variant: number; users: number; mesh: THREE.InstancedMesh | null }
/**
 * A tree (`flora`) or a rock (`rock`) of a stand, with its prototype, yaw and ground tufts (trees only). 63.4 step 8: a
 * tree's `form` follows the state — whole, damaged (at or under TREE_FORM.damagedAt of its health) or felled (a stump
 * where it stood and its trunk lying away from what felled it, `fallFrom`) — each form a prototype of its own.
 */
interface PropRec {
  flora?: FloraState; rock?: RockState; dir: Vec3; scale: number; proto: number; yaw: number;
  tufts: Array<{ proto: number; angle: number; dist: number; scale: number }>; hidden: boolean;
  chunk: number;
  form: 'whole' | 'damaged' | 'crownLost' | 'felled';
  /** the form prototypes, made when the tree first takes the form (-1: the species has none — a giant's kind), by form and level */
  forms: Record<string, number>;
  /** where the blow that felled it came from (the log's), null when unknown — a seeded fall then */
  fallFrom: Vec3 | null;
}
interface Chunk {
  key: string;
  planetIndex: number;
  /** indices into the props */
  items: number[];
  /** a sphere round every prop it holds (world units) and its direction on the planet with the cap it spans */
  center: THREE.Vector3;
  radius: number;
  dir: THREE.Vector3;
  cap: number;
  built: boolean;
  stale: boolean;
  tier: Tier;
  /** the tier it is drawn at this frame (TIER_NONE when out of the frustum or unbuilt) */
  drawn: Tier;
  mats: Float32Array;
  /** 63.4 step 8: each item's fallen trunk (the tree's seat, yawed along its fall) */
  fallMats: Float32Array;
  tuftMats: Float32Array;
  tuftLift: Float32Array;   // 67.50 (1): the relief under each tuft (the shader fades it in with the drawn fine ground)
  tuftProto: Int32Array;
  /** 62.61: when the streamer made it (the stands' clock; the grow-in), and whether it has dropped it */
  born?: number;
  evicted?: boolean;
  streamed?: boolean;
}

export interface TreeStandStats { trees: number; rocks: number; chunks: number; built: number; full: number; impostor: number; drawCalls: number; buildMs: number; prototypes: number; /** E5.4 step 2: the forest's trees filed so far, and the time spent making them */ forestTrees: number; forestMs: number }

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _yaw = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3();
const _Y = new THREE.Vector3(0, 1, 0), _n = new THREE.Vector3(), _p2 = new THREE.Vector3(), _qi = new THREE.Quaternion(), _r = new THREE.Vector3();
const _frustum = new THREE.Frustum(), _pv = new THREE.Matrix4(), _sphere = new THREE.Sphere();

/** Sway, glow and the shared clock: one Lambert program for every tree prototype (another for the tufts, double-sided). */
/**
 * 62.61: the stands' fades, shared by their materials — an instance shrinks to nothing over the last stretch before the
 * draw's cut (uCut: from, to; world units from the camera, uCamPos) instead of vanishing there, and grows in over uGrow
 * seconds from when its chunk was first drawn (aBorn, on the stands' clock): no pop at the edge, none as the streamer
 * makes a cell.
 */
interface StandsFade { uCamPos: { value: THREE.Vector3 }; uCut: { value: THREE.Vector2 }; uGrow: { value: number } }

/** 67.36: the wind in the stands (windSway.ts's uniforms but the clock, which is the stands' own). */
type StandsWind = Omit<WindSwayUniforms, 'uTime'>;

/** The stands' material (exported for the design sheets: 67.36's wind sheet draws a stand with it). */
export function standsMaterial(time: { value: number }, sway: number, side: THREE.Side, fade?: StandsFade, wind?: StandsWind): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true, side });
  m.onBeforeCompile = shader => {
    shader.uniforms.uTime = time;
    shader.uniforms.uSway = { value: sway };
    if (fade) Object.assign(shader.uniforms, fade);
    if (wind) Object.assign(shader.uniforms, wind);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', `attribute vec3 glow;\nvarying vec3 vGlow;\nuniform float uTime;\nuniform float uSway;\n${fade ? 'attribute float aBorn;\nuniform vec3 uCamPos;\nuniform vec2 uCut;\nuniform float uGrow;\n' : ''}${wind ? WIND_SWAY_HEAD + WIND_FRONT_GLSL : ''}void main() {`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
  vGlow = glow;
#ifdef USE_INSTANCING
  {
    vec3 base = instanceMatrix[3].xyz;
    float h = fract(sin(dot(base, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
    float ph = h * 6.2831853, sp = 0.6 + 0.8 * fract(h * 7.31);
    float ax = sin(uTime * sp + ph) * uSway, az = cos(uTime * sp * 0.7 + ph + 1.5) * uSway * 0.75;
    transformed.x -= position.y * az;
    transformed.z += position.y * ax;
${wind ? `    {   // 67.36: leaning downwind with the wind's level, bowed further as a gust's front reaches the tree
      vec3 up = normalize(instanceMatrix[1].xyz);
      vec3 w = uWindDir - up * dot(uWindDir, up);
      if (uWind > 0.0 && dot(w, w) > 1e-6) {
        w = normalize(w);
        vec3 wl = normalize(transpose(mat3(instanceMatrix)) * w);
        transformed += wl * (position.y * uWind * (${WIND_SWAY.tree.lean.toFixed(3)} + ${WIND_SWAY.tree.bow.toFixed(3)} * windFront(base, up, w, h)));
      }
    }` : ''}
    vGlow = glow * (0.75 + 0.25 * sin(uTime * sp * 0.8 + ph));
${fade ? '    transformed *= (1.0 - smoothstep(uCut.x, uCut.y, distance(base, uCamPos))) * smoothstep(aBorn, aBorn + uGrow, uTime);   // 62.61' : ''}
  }
#endif`);
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'varying vec3 vGlow;\nvoid main() {')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\n  totalEmissiveRadiance += vGlow;')
      // 67.31: a glow reads through the haze and the weather — most of what the fog took from it comes back (no light, no bloom)
      .replace('#include <fog_fragment>', '#include <fog_fragment>\n#ifdef USE_FOG\n  gl_FragColor.rgb += vGlow * 0.8 * fogFactor;\n#endif');
  };
  m.customProgramCacheKey = () => `tree-stands-${side}${fade ? '-fade' : ''}${wind ? '-wind' : ''}`;
  return m;
}

export class TreeStands {
  readonly person: number;
  private readonly props: PropRec[] = [];
  private readonly protos: Proto[] = [];
  private readonly tuftProtos: TuftProto[] = [];
  private readonly chunks: Chunk[] = [];
  private readonly time = { value: 0 };
  /** 67.36: the wind in the trees (`windFrom`); still until it is given one */
  private readonly wind: StandsWind;
  private readonly treeMat: THREE.MeshLambertMaterial;
  private readonly tuftMat: THREE.MeshLambertMaterial;
  private readonly rockMat: THREE.MeshLambertMaterial;
  private readonly deadPlanets = new Set<number>();
  /** 67.46 (5): the far woods past the stand-ins (rendering/farCanopy.ts), or null (the Lab's Far woods off, or no trees) */
  private canopy: FarCanopy | null = null;
  /** 67.61: the jungle's drawn layers (the planets with the jungle on; null: none) */
  private jungle: JungleField | null = null;
  private dirty = true;
  private buildMs = 0;
  /** 67.45: the chunks a frame visits, and where the camera stood when they were found (update) */
  private near: Int32Array | null = null;
  private readonly nearAt = new THREE.Vector3();
  private lastFlora: readonly FloraState[] | null = null;
  private lastRocks: readonly RockState[] | null = null;
  private lastHealth: GameState['floraHealth'] | null = null;
  private readonly protoIndex = new Map<string, number>();
  private readonly chunkIndex = new Map<string, number>();
  private readonly tuftIndex = new Map<string, number>();
  private readonly fine: FineGround | undefined;
  /** 69.24: the person's k (the bowls' depths), and each planet's bowls and pads as the chunks were last seated on them */
  private readonly k: number;
  private readonly bowlsSeen = new Map<number, { craterlets: PlanetState['craterlets']; digs: PlanetState['digs']; terraces: PlanetState['terraces'] }>();
  /** E5.4 step 2: the planets whose forest is drawn, and the cells already made */
  private readonly forest: Array<{ planetIndex: number; seed: string; n: number; keepOut: ForestKeepOut[]; basis: Float32Array; done: Set<number>; density: ForestDensityMode; windSeed: string; jungle: number }> = [];
  private forestTrees = 0;
  /** the forest's cells found empty (`${pi}:f${cell}`): not looked at again */
  private readonly emptyForest = new Set<string>();
  /** 62.61's fix: how many times a species' mesh grew */
  private grows = 0;
  /** 62.61: the near-field streamer the forest's cells come through (its own, unless the host shares one) */
  private streamer: NearFieldStreamer | null = null;
  private ownsStreamer = false;
  private lastPlanets: PlanetState[] = [];
  private readonly fade: StandsFade = { uCamPos: { value: new THREE.Vector3() }, uCut: { value: new THREE.Vector2(1e9, 2e9) }, uGrow: { value: STANDS_FADE.growS } };

  /**
   * The normal-size trees (`state.flora` and any `extra` drawn for a measurement) and rocks (`state.rocks`) of a
   * person-scale battle; `rockColor(planetIndex)` is the planet's rock colour (the old meshes' `propOptsFor`).
   */
  /** partial destruction step 3: the debris drawn beside the stands */
  private readonly debrisView: DebrisView;

  constructor(private readonly scene: THREE.Scene, state: Pick<GameState, 'flora' | 'planets' | 'config'> & Partial<Pick<GameState, 'rocks'>>, extra: readonly FloraState[] = [], rockColor: (planetIndex: number) => string = () => '#7a7468',
    /** E5.4 step 2: draw the planet's forest past the battle's own dressing (rendering/forestDraw.ts), from each planet's seed (158) */
    opts: { forest?: boolean; streamer?: NearFieldStreamer } = {}) {
    this.debrisView = new DebrisView(scene);
    const ps = personSetOf(state as GameState);
    this.person = PERSON_BODY_HEIGHT * ps.k;
    this.fine = ps;
    this.k = ps.k;
    state.planets.forEach((pl, i) => this.bowlsSeen.set(i, { craterlets: pl.craterlets, digs: pl.digs, terraces: pl.terraces }));
    this.wind = { uWind: { value: 0 }, uWindDir: { value: new THREE.Vector3(1, 0, 0) }, uGust: { value: WIND_SWAY.frontPeople * this.person }, uFront: { value: 0 } };
    this.treeMat = standsMaterial(this.time, TREE_SWAY, THREE.FrontSide, this.fade, this.wind);
    this.tuftMat = standsMaterial(this.time, 0, THREE.DoubleSide, this.fade);
    if (ps.fineAmplitude > 0) fineLiftMaterial(this.tuftMat);   // 67.50 (1): tufts ride the drawn fine ground
    this.rockMat = standsMaterial(this.time, 0, THREE.FrontSide, this.fade);
    for (const f of [...state.flora, ...extra]) {
      const planet = state.planets[f.planetIndex];
      if (f.size !== 'normal' || !planet) continue;
      this.fileTree(f, planet);
    }
    for (const r of state.rocks ?? []) {
      const planet = state.planets[r.planetIndex];
      if (r.size !== 'normal' || !planet) continue;
      const variant = rockVariant(r.id), color = rockColor(r.planetIndex);   // (core's draws: the hull the cover slots read, 66.9)
      const proto = this.protoFor(`rock|${r.rockType}|${color}|${variant}`, () => ({ kind: 'rock', type: r.rockType, biome: r.biome, color, variant }));
      this.fileProp({ rock: r, dir: r.surfaceDir, scale: r.scale, proto, yaw: rockYaw(r.id), tufts: [], hidden: !r.alive, chunk: -1, form: 'whole', forms: {}, fallFrom: null }, r.planetIndex, planet);
    }
    for (const c of this.chunks) this.finishChunk(c, state.planets);
    // E5.4 step 2: the planet's forest, drawn — its cells made round the camera by the near-field streamer (62.61)
    // E5.4: the planet forest drawn — with the sim's forest (GameConfig.planetForest) its planets, ground and keep-out
    // exactly (core/forestHeld.ts: the trees drawn are the trees held), else the same from the state at setup
    const held = (state as Partial<GameState>).forest;
    if ((opts.forest || held) && state.planets.length) {
      const dressed = held ? held.planets.map(p => p.planetIndex) : [...new Set(((state as Partial<GameState>).dressedAreas ?? []).map(a => a.planetIndex))].sort((a, b) => a - b);
      for (const pi of dressed) {
        const planet = state.planets[pi];
        if (!planet || biomesFor(planet).length === 0) continue;
        const hp = held?.planets.find(p => p.planetIndex === pi);
        const keepOut: ForestKeepOut[] = held ? held.keepOut.filter(d => d.planetIndex === pi).map(d => ({ dir: d.dir, angle: d.angle })) : forestKeepOutAt(state as GameState, pi, this.person);
        // decision 158: the planet's own seed; the ground at setup (a cell made after a crater is the one the sim holds)
        this.forest.push({ planetIndex: pi, seed: hp?.seed ?? planetForestSeed(planet), n: hp?.n ?? forestCellsPerEdge(planet, this.person), keepOut, basis: hp?.basis ?? planet.heightmap, done: new Set(), density: held?.density ?? state.config?.forestDensity ?? 'flat', windSeed: held?.windSeed ?? String(state.config?.seed ?? ''), jungle: held ? held.jungle ?? 0 : state.config?.forestJungle ?? 0 });
      }
      if (this.forest.length) {
        this.streamer = opts.streamer ?? new NearFieldStreamer();
        this.ownsStreamer = !opts.streamer;
        this.streamer.register(this.forestClient());
        // 67.61: the jungle's canopy, understory and vines over its stands — the streamer's client too, on its own reach
        const jungles = this.forest.filter(f => f.jungle > 0);
        if (jungles.length) { this.jungle = new JungleField(scene, this.person); this.streamer.register(this.jungle.client(jungles)); }
      }
    }
    // 62.61 step 2: the host's near field, shared — the stands' build queue claims from its ledger even with no forest
    if (!this.streamer && opts.streamer) this.streamer = opts.streamer;
    // 63.4 step 8: the forms the state already has (a stand built mid-battle: the Lab's switch, a re-entry)
    this.followState(state.flora, state.rocks, (state as Partial<GameState>).floraHealth, (state as Partial<GameState>).propEvents, !!state.config?.partialDestruction);
    // 67.46 (5): the far woods — a cap per forest stand and per chunk of the battle's own trees, past the stand-ins
    if (farWoodsDrawn() && (this.forest.length || this.props.some(p => p.flora))) {
      const dressed: Array<{ planetIndex: number; dir: Vec3; radius: number; biome: NonNullable<FloraState['biome']> }> = [];
      for (const c of this.chunks) {
        const biomes = new Map<NonNullable<FloraState['biome']>, number>();
        for (const ri of c.items) { const b = this.props[ri].flora?.biome; if (b) biomes.set(b, (biomes.get(b) ?? 0) + 1); }
        if (!biomes.size) continue;
        const biome = [...biomes.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))[0][0];
        dressed.push({ planetIndex: c.planetIndex, dir: { x: c.dir.x, y: c.dir.y, z: c.dir.z }, radius: c.radius, biome });
      }
      this.canopy = new FarCanopy(scene, state, this.person, this.forest, dressed, this.fade);
    }
  }

  /** 67.46 (5): the far woods' caps made so far, their triangles, and whether the build is done (null: off). */
  canopyStats(): { caps: number; triangles: number; done: boolean; buildMs: number } | null {
    return this.canopy ? { caps: this.canopy.caps, triangles: this.canopy.triangles, done: this.canopy.done, buildMs: +this.canopy.buildMs.toFixed(1) } : null;
  }

  /** How many normal trees this draws (the state's and any stress extras). */
  get treeCount(): number { return this.props.filter(p => p.flora).length; }

  /**
   * Each frame: tiers from the camera, culled by the frustum; chunks that came in range built within the budget,
   * nearest first; instance buffers repacked if anything changed; the sway's clock advanced.
   */
  /** A prototype by key (made on first use), its users counted. */
  private protoFor(key: string, make: () => Omit<Proto, 'key' | 'users' | 'geo' | 'full' | 'impostor' | 'height' | 'half'>): number {
    let pi = this.protoIndex.get(key);
    if (pi === undefined) {
      pi = this.protos.length; this.protoIndex.set(key, pi);
      this.protos.push({ key, ...make(), users: 0, geo: null, full: null, impostor: null, height: 0, half: 0 });
    }
    this.protos[pi].users++;
    return pi;
  }

  /** A normal tree filed with its prototype and tufts (in `chunkKey`'s chunk, else its cell's at the chunk size). */
  private fileTree(f: FloraState, planet: PlanetState, chunkKey?: string): void {
    const variant = treeVariant(f.id);
    const proto = this.protoFor(`tree|${f.floraType}|${f.biome ?? 'broadleaf'}|${variant}`, () => ({ kind: 'tree', type: f.floraType, biome: f.biome, color: '', variant }));
    const tufts = treeTufts(f).map(t => {
      const tv = t.seed % TREE_LOD.tuftVariants, tk = `${t.kind}|${tv}`;
      let ti = this.tuftIndex.get(tk);
      if (ti === undefined) { ti = this.tuftProtos.length; this.tuftIndex.set(tk, ti); this.tuftProtos.push({ key: tk, kind: t.kind, variant: tv, users: 0, mesh: null }); }
      this.tuftProtos[ti].users++;
      return { proto: ti, angle: t.angle, dist: t.dist, scale: t.scale };
    });
    this.fileProp({ flora: f, dir: f.surfaceDir, scale: f.scale, proto, yaw: treeYaw(f.id), tufts, hidden: false, chunk: -1, form: 'whole', forms: {}, fallFrom: null }, f.planetIndex, planet, chunkKey);
  }

  /** A prop into its chunk: `chunkKey`'s, else its cell's at a cell size near chunkPersons people. */
  private fileProp(rec: PropRec, planetIndex: number, planet: PlanetState, chunkKey?: string): void {
    const ri = this.props.length;
    this.props.push(rec);
    const ck = chunkKey ?? `${planetIndex}:${cellOf(rec.dir.x, rec.dir.y, rec.dir.z, chunkCellsPerEdge(planet.radius, this.person))}`;
    let ci = this.chunkIndex.get(ck);
    if (ci === undefined) {
      ci = this.chunks.length; this.chunkIndex.set(ck, ci);
      this.chunks.push({ key: ck, planetIndex, items: [], center: new THREE.Vector3(), radius: 0, dir: new THREE.Vector3(), cap: 0, built: false, stale: false, tier: TIER_NONE, drawn: TIER_NONE,
        mats: new Float32Array(0), fallMats: new Float32Array(0), tuftMats: new Float32Array(0), tuftLift: new Float32Array(0), tuftProto: new Int32Array(0) });
    }
    this.chunks[ci].items.push(ri);
    rec.chunk = ci;
  }

  /**
   * A chunk's bounds — a sphere round where its props stand (the ground's own heights), a tree's height above and a few
   * people more (the fine ground, a crater's change), and the cap its directions span — and its buffers.
   */
  private finishChunk(c: Chunk, planets: PlanetState[]): void {
    const pad = 0.45 * NORMAL_TREE_PERSONS * this.person * 1.2 + 3 * this.person;
    c.dir.set(0, 0, 0);
    c.center.set(0, 0, 0);
    const feet = c.items.map(ri => { const p = this.props[ri]; const w = p.flora ? getFloraWorldPosition(p.flora, planets) : getRockWorldPosition(p.rock!, planets); return new THREE.Vector3(w.x, w.y, w.z); });
    for (const ri of c.items) { const d = this.props[ri].dir; c.dir.x += d.x; c.dir.y += d.y; c.dir.z += d.z; }
    c.dir.normalize();
    for (const p of feet) c.center.add(p);
    c.center.multiplyScalar(1 / feet.length);
    let cap = 0, reach = 0;
    for (const ri of c.items) { const d = this.props[ri].dir; _n.set(d.x, d.y, d.z).normalize(); cap = Math.max(cap, c.dir.angleTo(_n)); }
    for (const p of feet) reach = Math.max(reach, p.distanceTo(c.center));
    c.cap = cap;
    c.radius = reach + pad;
    c.mats = new Float32Array(c.items.length * 16);
    c.fallMats = new Float32Array(c.items.length * 16);
    const tuftCount = c.items.reduce((sum, ri) => sum + this.props[ri].tufts.length, 0);
    c.tuftMats = new Float32Array(tuftCount * 16);
    c.tuftLift = new Float32Array(tuftCount);
    c.tuftProto = new Int32Array(tuftCount);
  }

  /**
   * E5.4 step 2 / 62.61: the forest's cells as the near-field streamer's client (rendering/nearField.ts): made round the
   * camera within the impostor's reach — nearest and in view first, under one budget, led along the camera's motion —
   * each its own chunk, tiered and drawn as the battle's stands are, grown in as it first draws; evicted past the drop
   * reach, its trees' records kept so a return draws the same without making them again. Drawn only: none of it is state.
   */
  private forestClient(): NearFieldClient<number> {
    const byPlanet = new Map(this.forest.map(f => [f.planetIndex, f]));
    return {
      name: 'forest',
      planets: () => this.forest.map(f => f.planetIndex).filter(pi => !this.deadPlanets.has(pi)),
      cellsPerEdge: pi => byPlanet.get(pi)!.n,
      // (62.61's follow-up, the owner's commander-view stutter: no more than the view can show — from the diorama it sees
      // ~340 people, and the cells to 888 round it were made, held and churned for nothing — with a stand and a cell to spare)
      reach: (_pi, view) => Math.min((TREE_LOD.impostor * (1 + TREE_LOD.hysteresis) + FOREST.cellPeople) * this.person, (view?.far ?? Infinity) + VIEW_REACH_MARGIN_PEOPLE * this.person),
      make: (pi, planet, cell) => {
        const ci = this.forestChunk(byPlanet.get(pi)!, planet, cell);
        if (ci === undefined) return null;
        const c = this.chunks[ci];
        this.finishChunk(c, this.lastPlanets);
        c.evicted = false; c.built = false; c.streamed = true;
        c.born = this.time.value;   // grown from its making: made out of view, it is whole when it turns into view
        this.near = null;   // the near pass takes it
        return ci;
      },
      evict: (_pi, _cell, ci) => {
        if (ci === null) return;
        const c = this.chunks[ci];
        c.evicted = true; c.built = false; c.stale = false; c.tier = TIER_NONE; c.born = undefined;
        if (c.drawn !== TIER_NONE) { c.drawn = TIER_NONE; this.dirty = true; }
        c.mats = new Float32Array(0); c.fallMats = new Float32Array(0);
        c.tuftMats = new Float32Array(0); c.tuftLift = new Float32Array(0); c.tuftProto = new Int32Array(0);
        this.near = null;
      },
    };
  }

  /**
   * A forest cell's chunk, its trees filed the first time either client asks (the forest's make, or the scatter's:
   * `forestCellOf`); undefined when the cell has none (remembered). Filed outside the forest's make, the chunk waits
   * untiered (`evicted`) until that make finishes it.
   */
  private forestChunk(f: (typeof this.forest)[number], planet: PlanetState, cell: number): number | undefined {
    const key = `${f.planetIndex}:f${cell}`;
    const ci = this.chunkIndex.get(key);
    if (ci !== undefined || this.emptyForest.has(key)) return ci;
    // (on the planet's ground at setup — the state's basis, as the held band places them: the sim's trees are the drawn ones)
    const trees = forestCell(basisPlanet(planet, f.basis), f.planetIndex, f.seed, this.person, f.n, cell, f.keepOut, f.density, f.windSeed, f.jungle);
    if (!trees.length) { this.emptyForest.add(key); return undefined; }
    for (const t of trees) this.fileTree(t, planet, key);
    this.forestTrees += trees.length;
    const made = this.chunkIndex.get(key)!;
    this.chunks[made].evicted = true;
    return made;
  }

  /** 62.61's follow-up (rendering/groundScatter.ts ForestScatterSource): the forest's planets and grids, for the scatter. */
  forestGrid(): ReadonlyArray<{ planetIndex: number; n: number }> { return this.forest.map(f => ({ planetIndex: f.planetIndex, n: f.n })); }

  /** A forest cell's trees (filed if they were not), the keep-out on its planet and the planet's forest seed; null off the forest. */
  forestCellOf(planetIndex: number, planet: PlanetState, cell: number): { trees: readonly FloraState[]; keepOut: readonly ForestKeepOut[]; seed: string } | null {
    const f = this.forest.find(e => e.planetIndex === planetIndex);
    if (!f) return null;
    const ci = this.forestChunk(f, planet, cell);
    const trees = ci === undefined ? [] : this.chunks[ci].items.map(ri => this.props[ri].flora!).filter(Boolean);
    return { trees, keepOut: f.keepOut, seed: f.seed };
  }

  /**
   * 67.36: the wind in the trees, from the grass's (GroundScatter.sway: the way and the level where the viewer is, the
   * gusts' fronts' travel) — copied each frame, so a front bows the stand as it crosses the meadow; the clock stays the
   * stands' own.
   */
  windFrom(u: WindSwayUniforms): void {
    this.wind.uWind.value = u.uWind.value; this.wind.uWindDir.value.copy(u.uWindDir.value);
    this.wind.uGust.value = u.uGust.value; this.wind.uFront.value = u.uFront.value;
  }

  update(
    camera: THREE.Camera, dt: number, planets: PlanetState[], flora?: readonly FloraState[], rocks?: readonly RockState[],
    /** 63.4 step 8: the trees' health and the blows' log (the damaged form, the fall's direction) */
    damage?: Pick<GameState, 'floraHealth' | 'propEvents'> & Partial<Pick<GameState, 'config' | 'debris' | 'structures' | 'players'>>,
  ): void {
    this.time.value += dt;
    const partial = !!damage?.config?.partialDestruction;
    // (67.45: the health as followState keeps it — none is null — or a state with no tree hurt followed itself every frame)
    if ((flora && flora !== this.lastFlora) || (rocks && rocks !== this.lastRocks) || (damage && (damage.floraHealth ?? null) !== this.lastHealth)) {
      this.followState(flora, rocks, damage?.floraHealth, damage?.propEvents, partial);
    }
    this.debrisView.update(planets, partial ? damage?.debris : undefined, partial ? damage : undefined);   // partial destruction step 3: what fell (step 7: a structure's shed parts as themselves)
    camera.updateMatrixWorld();
    _pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_pv);
    const cam = camera.getWorldPosition(_p);
    // 62.61: the fades' camera and cut; the forest's cells through the streamer (when the stands own it)
    this.fade.uCamPos.value.copy(cam);
    this.jungle?.follow(cam, this.time.value);   // (69.76: and the stands' clock, its cells' grow-in)
    const cutTo = TREE_LOD.impostor * this.person;
    this.fade.uCut.value.set(STANDS_FADE.cutFrom * cutTo, cutTo);
    this.lastPlanets = planets;
    if (this.streamer && this.ownsStreamer) this.streamer.update(camera, dt, planets, this.person);
    // 67.45: the chunks a frame visits — those within the impostor's reach and `nearMargin` people more, found by a pass
    // over all of them when the camera has moved more than the margin since the last (its centres stand still), with the
    // chunks still tiered: every other chunk is past the reach, untiered and undrawn, as a pass over all would leave it
    // (in the chunks' own order, so the builds' ties fall as they did). The cost is the near chunks', not the planet's
    const margin = TREE_LOD.nearMargin * this.person;
    if (!this.near || cam.distanceTo(this.nearAt) > margin) {
      const reach = TREE_LOD.impostor * (1 + TREE_LOD.hysteresis) * this.person + margin, near: number[] = [];
      for (let i = 0; i < this.chunks.length; i++) {
        const c = this.chunks[i];
        if (cam.distanceTo(c.center) - c.radius < reach || c.tier !== TIER_NONE || c.drawn !== TIER_NONE) near.push(i);
      }
      this.near = Int32Array.from(near); this.nearAt.copy(cam);
    }
    const want: Array<{ c: Chunk; gap: number }> = [];
    for (const i of this.near) {
      const c = this.chunks[i];
      const gap = Math.max(0, cam.distanceTo(c.center) - c.radius) / this.person;
      c.tier = this.deadPlanets.has(c.planetIndex) || c.evicted ? TIER_NONE : tierFor(gap, c.tier);
      if (c.tier !== TIER_NONE && (!c.built || c.stale)) want.push({ c, gap });
    }
    if (want.length) {
      want.sort((a, b) => a.gap - b.gap);
      // 62.61 step 2: with the host's near field shared, the queue claims its slice of the frame's ledger (its order unchanged)
      const shared = this.streamer && !this.ownsStreamer ? this.streamer : null;
      const cap = shared ? shared.claim(NEAR_FIELD.claims.stands) : TREE_LOD.budgetMs;
      const now = shared ? () => shared.time() : () => performance.now();   // (the ledger's own clock when shared)
      const t0 = now();
      for (const { c } of want) {
        this.buildChunk(c, planets);
        if (now() - t0 > cap) break;
      }
      const ms = now() - t0;
      this.buildMs += ms;
      shared?.spend('stands', ms);
    }
    // 67.46 (5): the far woods' build, on what the ledger leaves after the stands
    this.canopy?.update(planets, this.streamer ?? undefined, camera);
    // drawn this frame: in range, built (a chunk built just now included) and in view
    for (const i of this.near) {
      const c = this.chunks[i];
      let drawn: Tier = TIER_NONE;
      if (c.tier !== TIER_NONE && c.built) {
        _sphere.center.copy(c.center); _sphere.radius = c.radius + TREE_LOD.frustumMargin * this.person;
        if (_frustum.intersectsSphere(_sphere)) drawn = c.tier;
      }
      if (drawn !== c.drawn) { c.drawn = drawn; this.dirty = true; }
    }
    if (this.dirty) this.repack();
  }

  /** Build every chunk now (tests and sheets). */
  buildAll(planets: PlanetState[]): void { for (const c of this.chunks) if (!c.built || c.stale) this.buildChunk(c, planets); }

  /**
   * 69.24: each frame — where a planet's bowls or pads changed (a burst's craterlet, a dig, a terrace laid), the chunks over them
   * are seated again (their tufts' lift takes the bowls: they hung over a burst's bowl). The craters' coarse ground is
   * groundChanged's. Nothing when nothing changed (identity checks).
   */
  bowlsChanged(planets: PlanetState[]): void {
    planets.forEach((planet, pi) => {
      const was = this.bowlsSeen.get(pi);
      if (!was || (was.craterlets === planet.craterlets && was.digs === planet.digs && was.terraces === planet.terraces)) return;
      this.bowlsSeen.set(pi, { craterlets: planet.craterlets, digs: planet.digs, terraces: planet.terraces });
      const caps: Array<{ d: THREE.Vector3; reach: number }> = [];
      const diff = <T extends { id: number; dir: { x: number; y: number; z: number } }>(a: readonly T[] | undefined, b: readonly T[] | undefined, reach: (x: T) => number) => {
        const before = new Map((a ?? []).map(x => [x.id, x] as const)), after = new Map((b ?? []).map(x => [x.id, x] as const));
        for (const [id, x] of after) if (before.get(id) !== x) caps.push({ d: new THREE.Vector3(x.dir.x, x.dir.y, x.dir.z).normalize(), reach: reach(x) / planet.radius });
        for (const [id, x] of before) if (after.get(id) !== x) caps.push({ d: new THREE.Vector3(x.dir.x, x.dir.y, x.dir.z).normalize(), reach: reach(x) / planet.radius });
      };
      diff(was.craterlets, planet.craterlets, () => CRATERLET_REACH * this.person);
      diff(was.digs, planet.digs, () => 3 * this.person);
      diff(was.terraces, planet.terraces, t => terraceReach(t));
      if (!caps.length) return;
      for (const c of this.chunks) {
        if (c.planetIndex !== pi || !c.built) continue;
        if (caps.some(x => c.dir.angleTo(x.d) <= c.cap + x.reach)) this.buildChunk(c, planets);
      }
    });
  }

  /**
   * The ground moved on `planetIndex` (a crater). With the vertices it moved, the chunks over them are re-seated
   * now; without, every chunk on that planet is re-seated as it comes in range (until then it is drawn where it was).
   */
  groundChanged(planets: PlanetState[], planetIndex: number, modifiedVertices?: readonly number[]): void {
    const planet = planets[planetIndex];
    if (!planet) return;
    if (!modifiedVertices || modifiedVertices.length === 0) {
      for (const c of this.chunks) if (c.planetIndex === planetIndex && c.built) c.stale = true;
      return;
    }
    // the cap the edit spans: its vertices' mean direction and the widest angle from it
    const bp = planet.basePositions, d = new THREE.Vector3(), v = new THREE.Vector3();
    for (const i of modifiedVertices) d.x += bp[i * 3], d.y += bp[i * 3 + 1], d.z += bp[i * 3 + 2];
    if (d.lengthSq() === 0) return;
    d.normalize();
    let span = 0;
    for (const i of modifiedVertices) { v.set(bp[i * 3], bp[i * 3 + 1], bp[i * 3 + 2]).normalize(); span = Math.max(span, d.angleTo(v)); }
    // every facet round a moved vertex tilts, so trees up to a facet's edge beyond the edit move (a megaplanet's
    // facets are ~45 people across): the edge of an equilateral facet of the sphere's area / the facet count
    const facets = planet.heightmap.length / 3;
    const edge = Math.sqrt((16 * Math.PI) / (Math.sqrt(3) * Math.max(1, facets)));
    const margin = Math.max((4 * this.person) / planet.radius, 1.1 * edge);
    for (const c of this.chunks) {
      if (c.planetIndex !== planetIndex || !c.built) continue;
      if (c.dir.angleTo(d) <= c.cap + span + margin) this.buildChunk(c, planets);
    }
  }

  /**
   * Hide trees the viewer may not see (`hide(flora)` true — the fog); applied at the next frame. A felled tree is not
   * hidden by the state but drawn felled (63.4 step 8); rocks follow the state only.
   */
  setHidden(hide: (f: FloraState) => boolean): void {
    for (const t of this.props) {
      if (!t.flora) continue;
      const h = hide(t.flora);
      if (h !== t.hidden) { t.hidden = h; this.dirty = true; }
    }
  }

  /**
   * The state's flora, its trees' health or the rocks changed (a new array or record). 63.4 step 8: a tree takes its
   * form — felled (`alive` false: a stump and its trunk lying away from the blast that felled it, the log's `from`),
   * damaged (at or under TREE_FORM.damagedAt of its health) or whole — and its chunk is re-seated for a fall. A rock that
   * fell leaves its instance. Forms only ever advance (a tree never heals or stands up again).
   */
  private followState(flora?: readonly FloraState[], rocks?: readonly RockState[], health?: GameState['floraHealth'], events?: GameState['propEvents'], partial = false): void {
    let deadRocks: Set<number> | null = null;
    const trees = flora ? new Map(flora.map(f => [f.id, f])) : null;
    if (flora) this.lastFlora = flora;
    this.lastHealth = health ?? null;
    if (rocks && rocks !== this.lastRocks) { this.lastRocks = rocks; for (const r of rocks) if (r.size === 'normal' && !r.alive) (deadRocks ??= new Set()).add(r.id); }
    const full = PROPS.flora.health ?? 100;
    let felledFrom: Map<number, Vec3> | null = null;
    for (const t of this.props) {
      if (t.rock) { if (!t.hidden && deadRocks?.has(t.rock.id)) { t.hidden = true; this.dirty = true; } continue; }
      const f = trees?.get(t.flora!.id);
      if (!f) continue;
      // partial destruction step 3 (the switch on): the sim's forms by health — limbs lost, then the crown
      const hp = health?.[f.id] ?? full;
      const form: PropRec['form'] = !f.alive ? 'felled' : partial ? FORM_ORDER[propForm('flora', hp) + 1] : hp <= TREE_FORM.damagedAt * full ? 'damaged' : 'whole';
      if (form === t.form || FORM_ORDER.indexOf(form) < FORM_ORDER.indexOf(t.form)) continue;   // forms only advance
      t.form = form;
      if (form === 'felled') {
        if (!felledFrom) { felledFrom = new Map(); for (const e of events ?? []) if (e.felled && e.kind === 'flora' && e.from) felledFrom.set(e.id, e.from); }
        t.fallFrom = felledFrom.get(f.id) ?? null;
        const c = this.chunks[t.chunk];
        if (c?.built) c.stale = true;   // its trunk's fall is seated with the chunk
      }
      this.dirty = true;
    }
  }

  /** The prototype of a tree's form (made on first need; -1 when its species has none). */
  private formProto(t: PropRec, form: TreeForm, level?: number): number {
    const slot = level === undefined ? form : `${form}@${level}`;
    const known = t.forms[slot];
    if (known !== undefined) return known;
    const base = this.protos[t.proto];
    const key = `${base.key}|${slot}`;
    let pi = this.protoIndex.get(key);
    if (pi === undefined) {
      pi = this.protos.length;
      this.protoIndex.set(key, pi);
      this.protos.push({ key, kind: 'tree', type: base.type, biome: base.biome, color: '', variant: base.variant, form, ...(level !== undefined ? { level } : {}), users: base.users, geo: null, full: null, impostor: null, height: 0, half: 0 });
      this.ensureProto(this.protos[pi]);
      if (!this.protos[pi].geo) pi = -1;
    }
    t.forms[slot] = pi;
    return pi;
  }

  /** Everything on a destroyed planet leaves the scene. */
  hidePlanet(planetIndex: number): void { this.deadPlanets.add(planetIndex); this.dirty = true; }

  stats(): TreeStandStats {
    let full = 0, impostor = 0, draws = 0;
    for (const c of this.chunks) {
      if (c.drawn === TIER_FULL) full += c.items.length; else if (c.drawn === TIER_IMPOSTOR) impostor += c.items.length;
    }
    for (const p of this.protos) { if (p.full?.visible) draws++; if (p.impostor?.visible) draws++; }
    for (const t of this.tuftProtos) if (t.mesh?.visible) draws++;
    const trees = this.props.filter(p => p.flora).length;
    return { trees, rocks: this.props.length - trees, chunks: this.chunks.length, built: this.chunks.filter(c => c.built).length, full, impostor, drawCalls: draws, buildMs: +this.buildMs.toFixed(1), prototypes: this.protos.filter(p => p.geo).length, forestTrees: this.forestTrees, forestMs: this.streamer?.stats().ms ?? 0 };
  }

  dispose(): void {
    for (const p of this.protos) {
      for (const m of [p.full, p.impostor]) if (m) { this.scene.remove(m); m.dispose(); }
      p.geo?.full.dispose(); if (p.geo && p.geo.impostor !== p.geo.full) p.geo.impostor.dispose();
    }
    for (const t of this.tuftProtos) if (t.mesh) { this.scene.remove(t.mesh); t.mesh.geometry.dispose(); t.mesh.dispose(); }
    this.treeMat.dispose(); this.tuftMat.dispose(); this.rockMat.dispose();
    this.canopy?.dispose(); this.canopy = null;
    this.jungle?.dispose(); this.jungle = null;
    this.debrisView.dispose();
  }

  // -------------------------------------------------------------------------

  private ensureProto(p: Proto): void {
    if (p.geo) return;
    const seed = treeProtoSeed(p.type, p.variant);
    // 63.4 step 8: a form is lane E's, built as the whole tree is (same seed: the same tree, damaged or cut down)
    const model = p.form ? treeFormModel(p.type, p.biome, seed, p.form, p.level) : p.kind === 'tree' ? treeModel(p.type, p.biome, seed) : rockModel(p.type as RockKind, p.color, seed);
    if (!model) return;   // no form for this species (a giant's kind): the caller draws nothing for it
    const full = mergeModel(model) ?? new THREE.BoxGeometry(0.02, 0.2, 0.02);
    disposeModel(model);
    const box = full.boundingBox ?? new THREE.Box3().setFromBufferAttribute(full.attributes.position as THREE.BufferAttribute);
    p.height = box.max.y - Math.min(0, box.min.y);
    p.half = Math.max(box.max.x - box.min.x, box.max.z - box.min.z) / 2;
    // a stump and a fallen trunk are small and low: drawn whole at both tiers (a lathe impostor suits a standing tree)
    p.geo = { full, impostor: p.form === 'stump' || p.form === 'fallen' ? full : impostorGeometry(full) };
    const mat = p.kind === 'tree' ? this.treeMat : this.rockMat;
    p.full = this.instanced(p.geo.full, mat, p.users, `stands:${p.key}:full`);
    p.impostor = this.instanced(p.geo.impostor, mat, p.users, `stands:${p.key}:impostor`);
  }

  private ensureTuft(t: TuftProto): void {
    if (t.mesh) return;
    const model = buildFloraModel(t.kind as FloraState['floraType'], 1, 500 + t.variant * 104729).group;
    const geo = mergeModel(model) ?? new THREE.BoxGeometry(0.01, 0.01, 0.01);
    disposeModel(model);
    // 67.50 (1): each tuft's lift by the relief, beside its matrix (the material's fine-lift hook reads it)
    if (this.fine && this.fine.fineAmplitude > 0) geo.setAttribute('aFineLift', new THREE.InstancedBufferAttribute(new Float32Array(Math.max(1, t.users)), 1).setUsage(THREE.DynamicDrawUsage));
    t.mesh = this.instanced(geo, this.tuftMat, t.users, `stands:tuft:${t.key}`);
  }

  /**
   * 62.61's fix: an instanced mesh with room for half as many again (and at least `need`), its instances so far and the
   * per-instance attributes beside them (aBorn, the tufts' aFineLift) kept, in the old one's place in the scene.
   */
  private grown(mesh: THREE.InstancedMesh, need = mesh.count + 1): THREE.InstancedMesh {
    const cap = Math.max(need, Math.ceil(mesh.instanceMatrix.count * 1.5));
    const geo = mesh.geometry;
    for (const name of ['aBorn', 'aFineLift']) {
      const a = geo.getAttribute(name) as THREE.InstancedBufferAttribute | undefined;
      if (!a) continue;
      const b = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1).setUsage(THREE.DynamicDrawUsage);
      (b.array as Float32Array).set((a.array as Float32Array).subarray(0, Math.min(a.count, cap)));
      geo.setAttribute(name, b);
    }
    const m = this.instanced(geo, mesh.material as THREE.Material, cap, mesh.name);
    (m.instanceMatrix.array as Float32Array).set((mesh.instanceMatrix.array as Float32Array).subarray(0, mesh.count * 16));
    m.count = mesh.count; m.visible = mesh.visible;
    this.scene.remove(mesh);
    mesh.dispose();
    this.grows++;
    return m;
  }

  private instanced(geo: THREE.BufferGeometry, mat: THREE.Material, capacity: number, name: string): THREE.InstancedMesh {
    // 62.61: each instance's birth (its chunk's first draw) beside its matrix, for the grow-in
    if (!geo.getAttribute('aBorn')) geo.setAttribute('aBorn', new THREE.InstancedBufferAttribute(new Float32Array(Math.max(1, capacity)), 1).setUsage(THREE.DynamicDrawUsage));
    const m = new THREE.InstancedMesh(geo, mat, Math.max(1, capacity));
    m.name = name;
    m.count = 0;
    m.visible = false;
    m.frustumCulled = false;          // the chunks are the culling
    m.matrixAutoUpdate = false;
    m.userData.noBake = true;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scene.add(m);
    return m;
  }

  /**
   * Seat every prop of a chunk on the ground as it is now, as the old meshes were (groundSeat.ts): a tree grows
   * along the radial and sinks what its trunk needs on the tilt, its tufts laid on the ground's plane; a rock
   * takes the ground's frame across its footprint.
   */
  private buildChunk(c: Chunk, planets: PlanetState[]): void {
    const planet = planets[c.planetIndex];
    if (!planet) return;
    const S = NORMAL_TREE_PERSONS * this.person;
    const seating = propSeatingOn();
    const dummy = new THREE.Object3D();
    let k = 0;
    for (let i = 0; i < c.items.length; i++) {
      const t = this.props[c.items[i]];
      const p = this.protos[t.proto];
      this.ensureProto(p);
      const f = t.flora;
      const s = (f ? S : NORMAL_ROCK_PERSONS * this.person) * t.scale;
      if (seating && f) {
        const trunk = (TREE_TRUNK_SIZE[f.floraType]?.half ?? p.half * f.scale * 0.1) * S;
        seatGroup(dummy, planet, { planetIndex: c.planetIndex, dir: t.dir, kind: 'tree', half: p.half * s, height: p.height * s, trunk, fine: this.fine, seatHalf: 0 });   // (71.2: where the sim stands it)
      } else if (seating) {
        seatGroup(dummy, planet, { planetIndex: c.planetIndex, dir: t.dir, kind: 'rock', half: p.half * s, height: p.height * s, fine: this.fine, seatHalf: this.fine ? rockSeatHalf(t.rock!, this.fine) : 0 });
      } else {
        const w = f ? getFloraWorldPosition(f, planets) : getRockWorldPosition(t.rock!, planets);
        dummy.position.set(w.x, w.y, w.z);
        dummy.quaternion.setFromUnitVectors(_Y, _n.set(t.dir.x, t.dir.y, t.dir.z).normalize());
      }
      _q.copy(dummy.quaternion).multiply(_yaw.setFromAxisAngle(_Y, t.yaw));
      _m.compose(dummy.position, _q, _s.setScalar(s));
      _m.toArray(c.mats, i * 16);
      // 63.4 step 8: a felled tree's trunk lies along the model's +z, turned to the blast's push (away from where it came
      // from, in the tree's own ground frame), or a seeded way when nothing says
      if (f && t.form === 'felled') {
        let fall = hashUnit(f.id, 5) * Math.PI * 2;
        if (t.fallFrom) {
          const push = _p2.set(dummy.position.x - t.fallFrom.x, dummy.position.y - t.fallFrom.y, dummy.position.z - t.fallFrom.z).applyQuaternion(_qi.copy(dummy.quaternion).invert());
          if (push.x * push.x + push.z * push.z > 1e-18) fall = Math.atan2(push.x, push.z);
        }
        _q.copy(dummy.quaternion).multiply(_yaw.setFromAxisAngle(_Y, fall));
        _m.compose(dummy.position, _q, _s.setScalar(s));
        _m.toArray(c.fallMats, i * 16);
      }
      // the tufts lie on the ground's plane round the trunk's foot (the old meshes' `groundRotationWithin`)
      if (t.tufts.length) {
        const frame = groundFrame(planet, t.dir, 0);
        const ground = new THREE.Quaternion().setFromUnitVectors(_Y, _n.set(frame.normal.x, frame.normal.y, frame.normal.z));
        // (71.2: the trunk stands on the fine ground now — its seat over the mesh; the tufts are laid from the mesh under it,
        // as before, and the shader's lift below puts each on the drawn ground at its spot, not on the seat and again on that)
        const raised = this.fine && this.fine.fineAmplitude > 0 ? seatGround(planet, t.dir, this.fine, 0) - frame.radius : 0;
        const up = _r.set(t.dir.x, t.dir.y, t.dir.z).normalize();
        const foot = new THREE.Vector3().copy(dummy.position).addScaledVector(up, -raised);
        for (const tf of t.tufts) {
          this.ensureTuft(this.tuftProtos[tf.proto]);
          const off = new THREE.Vector3(Math.cos(tf.angle) * tf.dist * S, 0, Math.sin(tf.angle) * tf.dist * S).applyQuaternion(ground);
          off.add(foot);
          // 67.50 (1): the relief at its spot — a trunk enters the hummocks (no flattening round it), so its grass rides
          // them where the fine ground is drawn (the shader's lift, faded as the patch fades: groundScatter.ts fineLiftMaterial)
          _r.set(off.x - planet.position.x, off.y - planet.position.y, off.z - planet.position.z).normalize();
          // (67.50 (3): the shader adds the far lift where the patch has faded out)
          // (69.24: and the bowls and the terraces under it, as the sim's ground has them — a tuft hung over a burst's bowl)
          c.tuftLift[k] = this.fine && this.fine.fineAmplitude > 0 ? fineGroundLift(planet, _r, this.fine, this.k, getSurfaceRadius(planet, _r)) : 0;
          _m.compose(off, ground, _s.setScalar(S * tf.scale));
          _m.toArray(c.tuftMats, k * 16);
          c.tuftProto[k++] = tf.proto;
        }
      }
    }
    c.built = true;
    c.stale = false;
    this.dirty = true;
  }

  /** Fill each prototype's instance buffer from the chunks drawn at each tier (only on a change). */
  private repack(): void {
    this.dirty = false;
    for (const p of this.protos) { if (p.full) p.full.count = 0; if (p.impostor) p.impostor.count = 0; }
    for (const t of this.tuftProtos) if (t.mesh) t.mesh.count = 0;
    // (67.45: a drawn chunk is always a near one — update — so the near ones, in the same order, are all it needs)
    const visit = this.near ?? this.chunks.map((_, i) => i);
    for (const ci of visit) {
      const c = this.chunks[ci];
      if (c.drawn === TIER_NONE) continue;
      let k = 0;
      for (let i = 0; i < c.items.length; i++) {
        const t = this.props[c.items[i]];
        const nt = t.tufts.length;
        if (t.hidden) { k += nt; continue; }
        const put = (pi: number, mats: Float32Array) => {
          const p = this.protos[pi];
          let mesh = c.drawn === TIER_FULL ? p.full : p.impostor;
          if (!mesh) return;
          // 62.61's fix: a species' mesh is made with room for the trees it had then; the forest files more after — grow it
          // (a draw past its buffer failed: the whole species vanished, the owner on the bike)
          if (mesh.count >= mesh.instanceMatrix.count) { mesh = this.grown(mesh); if (c.drawn === TIER_FULL) p.full = mesh; else p.impostor = mesh; }
          copy16(mats, i * 16, mesh.instanceMatrix.array as Float32Array, mesh.count * 16); bornAt(mesh, mesh.count, c.born); mesh.count++;
        };
        // 63.4 step 8: the tree's form — whole, damaged, or a stump and its fallen trunk (a species without forms: the
        // whole tree while it stands, nothing once felled)
        if (t.form === 'whole') put(t.proto, c.mats);
        else if (t.form === 'damaged') { const d = this.formProto(t, 'damaged'); put(d >= 0 ? d : t.proto, c.mats); }
        else if (t.form === 'crownLost') {
          // partial destruction step 3: the crown gone — lane E's damaged model at the table's level for the form
          const d = this.formProto(t, 'damaged', PROPS.flora.forms?.[1]?.level);
          put(d >= 0 ? d : t.proto, c.mats);
        }
        else {
          const st = this.formProto(t, 'stump'), fa = this.formProto(t, 'fallen');
          if (st >= 0) put(st, c.mats);
          if (fa >= 0) put(fa, c.fallMats);
        }
        if (c.drawn === TIER_FULL) {
          for (let j = 0; j < nt; j++, k++) {
            const tp = this.tuftProtos[c.tuftProto[k]];
            if (tp.mesh && tp.mesh.count >= tp.mesh.instanceMatrix.count) tp.mesh = this.grown(tp.mesh);   // (as a species' mesh: grown, not overrun)
            const tm = tp.mesh;
            if (tm) {
              copy16(c.tuftMats, k * 16, tm.instanceMatrix.array as Float32Array, tm.count * 16);
              bornAt(tm, tm.count, c.born);
              const lift = tm.geometry.getAttribute('aFineLift') as THREE.InstancedBufferAttribute | undefined;
              if (lift) { (lift.array as Float32Array)[tm.count] = c.tuftLift[k]; lift.needsUpdate = true; }
              tm.count++;
            }
          }
        } else k += nt;
      }
    }
    const done = (m: THREE.InstancedMesh | null) => {
      if (!m) return;
      m.visible = m.count > 0;
      if (m.count > 0) { m.instanceMatrix.clearUpdateRanges(); m.instanceMatrix.addUpdateRange(0, m.count * 16); m.instanceMatrix.needsUpdate = true; }
    };
    for (const p of this.protos) { done(p.full); done(p.impostor); }
    for (const t of this.tuftProtos) done(t.mesh);
  }
}

function copy16(from: Float32Array, at: number, to: Float32Array, into: number): void { for (let j = 0; j < 16; j++) to[into + j] = from[at + j]; }

/** The seed a prototype's model is built with (E5.2: rendering/treeShapeFit.ts measures the same trees). */
export function treeProtoSeed(type: string, variant: number): number { return 1000 + variant * 7919 + hashSeed(type); }
function hashSeed(s: string): number { let h = 0; for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0; return Math.abs(h) % 100000; }

/** A prototype's source model is thrown away once merged (the kit's materials are shared: only geometries go). */
function disposeModel(root: THREE.Object3D): void {
  root.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh || (o as THREE.Points).isPoints) (m.geometry as THREE.BufferGeometry | undefined)?.dispose(); });
}

// ---------------------------------------------------------------------------
// Dev: more trees than a battle has, to measure the drawing
// ---------------------------------------------------------------------------

/**
 * Dev only (`?treeStress=N`): extra trees drawn round the battle's own, so the drawing can be measured at a
 * few hundred to a few thousand trees (a stand density saturates near 350 — the site fills up). Presentation
 * only: they are never in the state, so nothing walks into them, shoots them or sees past them. Copies of the
 * battle's normal trees, each moved up to 12 people from its original, none within 3 people of a unit.
 */
export function stressTrees(state: Pick<GameState, 'flora' | 'players' | 'planets' | 'config'>, total: number, seed = 'tree-stress'): FloraState[] {
  const base = state.flora.filter(f => f.size === 'normal');
  if (base.length === 0 || total <= base.length) return [];
  const rng = new SeededRandom(seed);
  const person = PERSON_BODY_HEIGHT * personSetOf(state as GameState).k;
  const out: FloraState[] = [];
  let id = 1_000_000;
  for (let tries = 0; out.length < total - base.length && tries < total * 20; tries++) {
    const t = base[rng.int(0, base.length - 1)];
    const P = person / state.planets[t.planetIndex].radius;
    const x = t.surfaceDir.x + rng.range(-12, 12) * P, y = t.surfaceDir.y + rng.range(-12, 12) * P, z = t.surfaceDir.z + rng.range(-12, 12) * P;
    const l = Math.hypot(x, y, z);
    const d = { x: x / l, y: y / l, z: z / l };
    const nearUnit = state.players.some(u => {
      if (u.planetIndex !== t.planetIndex) return false;
      const ul = Math.hypot(u.surfaceDir.x, u.surfaceDir.y, u.surfaceDir.z) || 1;
      return Math.acos(Math.min(1, (d.x * u.surfaceDir.x + d.y * u.surfaceDir.y + d.z * u.surfaceDir.z) / ul)) < 3 * P;
    });
    if (nearUnit) continue;
    out.push({ ...t, id: id++, surfaceDir: d });
  }
  return out;
}

/** The page's `?treeStress=N` (0 when absent). */
export function treeStressParam(): number {
  try { return typeof location === 'undefined' ? 0 : Math.max(0, Math.floor(Number(new URLSearchParams(location.search).get('treeStress') ?? 0) || 0)); }
  catch { return 0; }
}
